<!--
  Command Center view "Pipeline Board" (stored value "pipelineBoard"): loans on a board whose columns
  the user builds from loan data (milestone, loan type, dates, amounts, people), each column a set of
  filters.

  DATA: api/PipelineBoard/Options and /Query (GSWebAPIv2 PipelineBoardService). The server takes the
  user from the token, offers only whitelisted ReportingView columns that really exist, passes every
  filter value as a SQL parameter, and scopes loans by the Insights rules (Corp: all; Insights Branch
  Manager: their branches; everyone else: their own loans). Totals are the server's COUNT and SUM over
  every matching loan, not the cards drawn.

  Columns are saved to the person's account (helpers/connect/boardStore.js, board "loans"; browser copy as
  fallback). "View as" is honoured: the server shows the viewed person's loans and columns (GSDesign's
  impersonation rules decide who may view as whom), and column changes aren't saved while viewing as.
  Cards open the loan in swiftApp. Nothing on this board changes a loan.
-->
<template>
  <div class="cx pbd">
    <section class="cx-card cx-card-pad pbd-board" aria-label="Pipeline board">
      <div class="cx-head">
        <div>
          <h2 class="cx-h2">Pipeline Board</h2>
          <div v-if="scopeText" class="cx-small cx-muted">{{ scopeText }}</div>
        </div>
        <div style="display: flex; gap: 8px; flex-wrap: wrap;">
          <button type="button" class="cx-btn cx-btn--sm" :disabled="!fields.length" :title="loadingFields ? 'Loading the loan fields…' : ''" @click="editing = 'new'">
            <i v-if="loadingFields" class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
            {{ loadingFields ? 'Loading fields…' : '+ Add column' }}
          </button>
          <button type="button" class="cx-btn cx-btn--sm" :disabled="anyLoading" @click="load">
            <i class="fa-light fa-arrows-rotate" aria-hidden="true"></i> Refresh
          </button>
        </div>
      </div>

      <div v-if="notDeployed" class="cx-banner cx-banner--warn" role="status">
        <i class="fa-light fa-plug" aria-hidden="true"></i>
        <span>The Pipeline Board API isn't on this server yet. It's in GSWebAPIv2 (PipelineBoardController) and needs a build and deploy.</span>
      </div>
      <div v-else-if="optionsError" class="cx-banner cx-banner--error" role="alert">
        <i class="fa-solid fa-circle-exclamation"></i>
        <span style="flex: 1;">{{ optionsError }}</span>
        <button type="button" class="cx-btn cx-btn--sm" @click="load">Try again</button>
      </div>
      <div v-if="boardNote && !notDeployed" class="cx-banner cx-banner--warn" role="status"><i class="fa-light fa-circle-info" aria-hidden="true"></i> <span>{{ boardNote }}</span></div>

      <div v-if="!notDeployed" class="cx-board">
        <section v-for="(col, i) in columns" :key="col.id" class="cx-col" :aria-label="col.title">
          <div class="cx-colh">
            <span class="cx-dot" :style="{ background: colorFor(i) }"></span>
            <span style="flex: 1; min-width: 0;">{{ col.title }}</span>
            <span class="cxd-colbtns">
              <button type="button" :aria-label="`Edit ${col.title}`" title="Edit column" @click="editing = col"><i class="fa-light fa-pen" aria-hidden="true"></i></button>
              <button type="button" :disabled="i === 0" :aria-label="`Move ${col.title} left`" title="Move left" @click="moveColumn(i, -1)">‹</button>
              <button type="button" :disabled="i === columns.length - 1" :aria-label="`Move ${col.title} right`" title="Move right" @click="moveColumn(i, 1)">›</button>
            </span>
          </div>
          <div class="pbd-sub cx-small cx-muted">
            <span>{{ col.loading && !col.rows.length ? '…' : `${col.total.toLocaleString()} loans · ${short(col.volume)}` }}</span>
            <span v-if="describe(col)" class="pbd-rule" :title="describe(col)">{{ describe(col) }}</span>
          </div>
          <div v-if="col.loading && !col.rows.length" class="cx-skel" aria-hidden="true"><span style="width: 90%"></span><span style="width: 70%"></span><span style="width: 80%"></span></div>
          <div v-else-if="col.error" class="cx-banner cx-banner--error" role="alert">{{ col.error }}</div>
          <p v-else-if="!col.rows.length" class="cx-small cx-muted" style="margin: 4px;">No loans.</p>
          <a v-for="r in col.rows" :key="r.loan_id || r.loan_number" class="cx-kc" :href="loanUrl(r.loan_id) || null" target="_blank" rel="noopener" @click="openLoan($event, r)">
            <span :style="{ background: colorFor(i) }"></span>
            <span class="cx-kb">
              <span class="cx-kn">{{ r.borrower || 'No borrower name' }}</span>
              <span class="cx-km">{{ [r.loan_number, money(r.loan_amount)].filter(Boolean).join(' · ') }}</span>
              <span class="cx-kr">
                <span v-if="r.milestone" class="cx-pill cx-pill--blue">{{ r.milestone }}</span>
                <span v-if="r.est_closing" class="cx-pill">Closes {{ fmtDate(r.est_closing, { month: 'short', day: 'numeric' }) }}</span>
                <span v-if="r.loan_type" class="cx-pill">{{ r.loan_type }}</span>
              </span>
            </span>
          </a>
          <button v-if="col.total > col.rows.length" type="button" class="cx-link-btn" :disabled="col.loading" @click="loadMore(col)">
            {{ col.loading ? 'Loading…' : `Show ${Math.min(PAGE, col.total - col.rows.length)} more` }}
          </button>
        </section>
        <p v-if="!columns.length" class="cx-small cx-muted">No columns yet. Use "+ Add column" to build one from loan fields.</p>
      </div>
      <p class="cx-small cx-muted" style="margin: 8px 2px 0;">{{ [whereLabel(boardWhere), 'Click a loan to open it in swiftApp.'].filter(Boolean).join(' ') }}</p>
    </section>

    <BoardColumnEditor
      v-if="editing"
      :column="editing === 'new' ? null : editing"
      :fields="fields"
      :ops-by-kind="LOAN_OPS"
      :extra-choices="choices"
      noun="loans"
      singular="loan"
      @save="onSave"
      @remove="onRemove"
      @cancel="editing = null"
      @field="loadChoices"
    />
  </div>
</template>

<script>
import '@/assets/css/connect.css'
import api from '@/api/connect.service.js'
import BoardColumnEditor from '@/components/connect/BoardColumnEditor.vue'
import { openLoanInWindow } from '@/helpers/openLoanWindow.js'
import { columnColor, fmtDate, money, swiftLoanUrl } from '@/helpers/connect/contactRules.js'
import { cleanColumn, newColumnId, describeFilters, LOAN_OPS } from '@/helpers/connect/boardColumns.js'
import { loadBoard, saveBoard, whereLabel } from '@/helpers/connect/boardStore.js'

const BOARD = 'loans'
const PAGE = 25
// A first board for someone who hasn't built one. Each is plain filters, editable like any column.
const DEFAULT_COLUMNS = [
  { title: 'Closing in the next 14 days', filters: [{ field: 'est_closing', op: 'in_next_days', value: '14' }] },
  { title: 'Locked in the last 30 days', filters: [{ field: 'lock_date', op: 'in_last_days', value: '30' }] },
  { title: 'Funded in the last 30 days', filters: [{ field: 'funded', op: 'in_last_days', value: '30' }] }
]

export default {
  name: 'PipelineBoardDashboard',
  components: { BoardColumnEditor },
  data() {
    return {
      LOAN_OPS,
      PAGE,
      fields: [],
      scopeText: '',
      optionsError: '',
      loadingFields: false,
      // Pick lists, fetched one field at a time when a field is picked in the editor: { fieldKey: [values] }
      choices: {},
      notDeployed: false,
      columns: [],
      editing: null,
      boardWhere: '',
      boardNote: '',
      seq: 0
    }
  },
  computed: {
    anyLoading() { return this.columns.some(c => c.loading) }
  },
  mounted() { this.load() },
  methods: {
    colorFor: columnColor,
    fmtDate,
    money,
    whereLabel,
    loanUrl: swiftLoanUrl,
    describe(col) { return describeFilters(col.filters, this.fields, LOAN_OPS) },
    short(v) {
      const n = Number(v || 0)
      if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`
      if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`
      if (n >= 1e3) return `$${Math.round(n / 1e3)}K`
      return `$${Math.round(n)}`
    },
    openLoan(e, r) {
      const url = swiftLoanUrl(r.loan_id)
      if (!url) { e.preventDefault(); return }
      if (openLoanInWindow(url, e)) e.preventDefault()
    },
    live(c) { return { ...cleanColumn(c), rows: [], total: 0, volume: 0, size: PAGE, loading: true, error: '' } },

    // The field list (for the editor) and the saved columns load side by side: a column's loans don't
    // depend on the field list, so a slow or failed field list no longer leaves the whole board empty.
    async load() {
      const seq = ++this.seq
      this.optionsError = ''
      this.notDeployed = false
      this.loadingFields = true
      const fieldsDone = this.loadFields(seq)
      const stored = await loadBoard(BOARD)
      if (seq !== this.seq) return
      if (this.notDeployed) return
      this.boardWhere = stored.where
      this.boardNote = stored.note
      const defs = stored.columns || DEFAULT_COLUMNS.map(c => ({ ...c, id: newColumnId() }))
      this.columns = defs.map(c => this.live(c))
      if (!stored.columns && !stored.viewingAs) this.persist()
      await Promise.all([fieldsDone, ...this.columns.map(col => this.loadColumn(col, seq))])
    },

    async loadFields(seq) {
      try {
        const options = await api.pipelineBoardOptions()
        if (seq !== this.seq) return
        this.fields = options?.fields || []
        this.scopeText = options?.scope || ''
        // An older API still sends the pick lists inline; keep them.
        this.fields.forEach(f => { if (f.choices && f.choices.length) this.choices[f.key] = f.choices })
      } catch (e) {
        if (seq !== this.seq) return
        if (e.status === 404) { this.notDeployed = true; this.columns = []; return }
        this.optionsError = e.status === 504 || e.status === 502 || /timeout/i.test(e.message || '')
          ? "The loan fields took too long to load, so you can't add or edit columns yet. Your columns below still work."
          : (e.message || "Couldn't load the loan fields.")
      } finally {
        if (seq === this.seq) this.loadingFields = false
      }
    },

    // Pick list for one choice field, fetched once per field and kept for the session.
    async loadChoices(key) {
      const def = this.fields.find(f => f.key === key)
      if (!def || def.kind !== 'choice' || this.choices[key]) return
      this.choices[key] = []
      try {
        this.choices[key] = (await api.pipelineBoardChoices(key)) || []
      } catch (e) {
        // No pick list (older API, or the lookup failed): the editor falls back to a text box.
        this.choices[key] = []
      }
    },

    async loadColumn(col, seq = this.seq) {
      col.loading = true
      col.error = ''
      try {
        const res = await api.pipelineBoardQuery({ filters: col.filters, sort: 'est_closing', sort_dir: 'asc', page: 1, page_size: col.size })
        if (seq !== this.seq) return
        col.rows = res?.rows || []
        col.total = res?.total ?? col.rows.length
        col.volume = res?.volume || 0
      } catch (e) {
        if (seq === this.seq) col.error = e.message
      } finally {
        if (seq === this.seq) col.loading = false
      }
    },
    loadMore(col) {
      col.size += PAGE
      this.loadColumn(col)
    },

    async persist() {
      const r = await saveBoard(BOARD, this.columns)
      this.boardWhere = r.where
      this.boardNote = r.note
    },
    moveColumn(i, delta) {
      const j = i + delta
      if (j < 0 || j >= this.columns.length) return
      const next = this.columns.slice()
      const [c] = next.splice(i, 1)
      next.splice(j, 0, c)
      this.columns = next
      this.persist()
    },
    onSave(def) {
      const i = this.columns.findIndex(c => c.id === def.id)
      const col = this.live(def)
      if (i >= 0) this.columns.splice(i, 1, col)
      else this.columns.push(col)
      this.editing = null
      this.persist()
      this.loadColumn(this.columns.find(c => c.id === def.id))
    },
    onRemove(id) {
      this.columns = this.columns.filter(c => c.id !== id)
      this.editing = null
      this.persist()
    }
  }
}
</script>

<style scoped>
.pbd { display: flex; flex-direction: column; gap: 14px; width: 100%; }
.pbd-board { min-width: 0; }
.pbd-sub { display: flex; flex-direction: column; gap: 2px; margin: -4px 4px 4px; }
.pbd-rule { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cxd-colbtns { display: inline-flex; gap: 0; margin-left: 2px; }
.cxd-colbtns button { border: 0; background: transparent; color: #6b7280; width: 22px; height: 22px; border-radius: 6px; font-size: 13px; line-height: 1; cursor: pointer; padding: 0; }
.cxd-colbtns button:hover:not(:disabled) { background: #e2e5ea; color: #111827; }
.cxd-colbtns button:disabled { opacity: .3; cursor: default; }
</style>
